import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	TextField,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import { getPgErrorMessage, readPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgPaymentDetails } from 'services/api/paymentGateway/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { PgFields, PgJsonView } from '../common/PgFields';
import { PgMiniTable, PgStatusChip } from '../common/PgMiniTable';
import { saveBlob } from '../common/helpers';
import { humanize } from '../common/tableHelpers';
import { formatDate, formatDateTime, formatMoney, formatPercent } from '../utils';
import { EasSyncDialog } from './EasSyncDialog';
import { RefundDialog } from './RefundDialog';
import { chargedCurrencyOf, hasOpenDispute, isRefundablePurpose, refundableOf } from './utils';

type TPaymentDialogProps = {
	/** Public id of the payment; `null` — closed. */
	transactionId: string | null;
	onClose: () => void;
	/** The payment was changed by an action: lists should re-read. */
	onChanged?: () => void;
};

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
	<Box sx={{ mt: 2 }}>
		<Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
			{title}
		</Typography>
		{children}
	</Box>
);

/**
 * Payment card (03 §4.8): everything the gateway knows about one payment and the actions on it —
 * sync with Airwallex, refund, EAS sync, receipt.
 */
export const PaymentDialog: FC<TPaymentDialogProps> = ({ transactionId, onClose, onChanged }) => {
	const [payment, setPayment] = useState<TPgPaymentDetails | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [acting, setActing] = useState<string | null>(null);
	const [refundOpen, setRefundOpen] = useState(false);
	const [easSyncOpen, setEasSyncOpen] = useState(false);
	const [sendOpen, setSendOpen] = useState(false);
	const [sendTo, setSendTo] = useState('');

	const load = useCallback(async (id: string) => {
		setLoading(true);
		setError(null);
		try {
			setPayment(await api.paymentGateway.getPayment(id));
		} catch (e) {
			setError(getPgErrorMessage(e, 'Failed to load the payment'));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		setPayment(null);
		setError(null);
		setRefundOpen(false);
		setEasSyncOpen(false);
		setSendOpen(false);
		if (transactionId) void load(transactionId);
	}, [transactionId, load]);

	const changed = (next?: TPgPaymentDetails) => {
		if (next) setPayment(next);
		else if (transactionId) void load(transactionId);
		onChanged?.();
	};

	const sync = async () => {
		if (!transactionId) return;
		setActing('sync');
		try {
			changed(await api.paymentGateway.syncPayment(transactionId));
			notificationContainer('Payment re-read from Airwallex', 'success');
		} catch (e) {
			notificationContainer(getPgErrorMessage(e, 'Sync failed'), 'error');
		} finally {
			setActing(null);
		}
	};

	const convert = async () => {
		if (!transactionId) return;
		setActing('convert');
		try {
			await api.paymentGateway.convertPayment(transactionId);
			notificationContainer('FX conversion started', 'success');
			changed();
		} catch (e) {
			notificationContainer(getPgErrorMessage(e, 'Failed to start the conversion'), 'error');
		} finally {
			setActing(null);
		}
	};

	const downloadReceipt = async () => {
		if (!transactionId) return;
		setActing('receipt');
		try {
			const file = await api.paymentGateway.downloadReceipt(transactionId);
			saveBlob(file.blob, file.fileName);
		} catch (e) {
			notificationContainer(await readPgErrorMessage(e, 'Failed to download the receipt'), 'error');
		} finally {
			setActing(null);
		}
	};

	const sendReceipt = async () => {
		if (!transactionId) return;
		setActing('send');
		try {
			const sent = await api.paymentGateway.sendReceipt({
				transactionId,
				body: { toEmail: sendTo.trim() || null },
			});
			notificationContainer(
				sent ? 'Receipt sent' : 'Nothing was sent: no recipient',
				sent ? 'success' : 'warning',
			);
			setSendOpen(false);
			changed();
		} catch (e) {
			notificationContainer(getPgErrorMessage(e, 'Failed to send the receipt'), 'error');
		} finally {
			setActing(null);
		}
	};

	const p = payment;
	const refundable = p ? refundableOf(p) : 0;
	const canRefund = !!p && p.status === 'succeeded' && isRefundablePurpose(p) && refundable > 0;
	const canEasSync = !!p && (p.easSyncStatus === 'conflict' || p.easSyncStatus === 'failed');
	const canConvert = !!p && p.status === 'succeeded' && p.currency !== 'EUR' && !p.fxConversion;
	const disputed = !!p && hasOpenDispute(p);

	return (
		<>
			<Dialog open={!!transactionId} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
				<DialogTitle color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					Payment
					{p && <PgStatusChip value={p.status} />}
					<Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
						{transactionId}
					</Typography>
				</DialogTitle>
				<DialogContent dividers>
					{loading && !p && <CircularProgress size={24} />}
					{error && <Alert severity="error">{error}</Alert>}
					{p && (
						<>
							<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
								<Button
									size="small"
									variant="outlined"
									onClick={() => void sync()}
									disabled={!!acting}
									startIcon={acting === 'sync' ? <CircularProgress size={14} /> : undefined}
								>
									Sync with Airwallex
								</Button>
								<Button
									size="small"
									variant="contained"
									color="error"
									onClick={() => setRefundOpen(true)}
									disabled={!canRefund || !!acting}
								>
									Refund
								</Button>
								{canEasSync && (
									<Button
										size="small"
										variant="contained"
										color="warning"
										onClick={() => setEasSyncOpen(true)}
										disabled={!!acting}
									>
										EAS sync
									</Button>
								)}
								{canConvert && (
									<Button
										size="small"
										variant="outlined"
										onClick={() => void convert()}
										disabled={!!acting}
									>
										Convert to EUR
									</Button>
								)}
								<Button
									size="small"
									variant="outlined"
									onClick={() => void downloadReceipt()}
									disabled={p.status !== 'succeeded' || !!acting}
									startIcon={acting === 'receipt' ? <CircularProgress size={14} /> : undefined}
								>
									Download receipt
								</Button>
								<Button
									size="small"
									variant="outlined"
									onClick={() => {
										setSendTo('');
										setSendOpen(true);
									}}
									disabled={p.status !== 'succeeded' || !!acting}
								>
									Send receipt
								</Button>
							</Box>

							{p.failureMessage && (
								<Alert severity="error" sx={{ mb: 1 }}>
									{p.failureMessage}
									{p.failureCode ? ` (${p.failureCode})` : ''}
								</Alert>
							)}
							{disputed && (
								<Alert severity="warning" sx={{ mb: 1 }}>
									Dispute: {p.disputeStatus ?? '—'}
									{p.disputeAmount !== null
										? `, ${formatMoney(p.disputeAmount, p.disputeCurrency)}`
										: ''}
									{p.lateFailureAt ? `. Late failure at ${formatDateTime(p.lateFailureAt)}.` : ''}
								</Alert>
							)}
							{canEasSync && (
								<Alert severity="warning" sx={{ mb: 1 }}>
									The &quot;paid&quot; mark did not reach EAS ({humanize(p.easSyncStatus)}). Paying
									the invoice again is blocked until you resolve it with &quot;EAS sync&quot;.
								</Alert>
							)}

							<Section title="Payment">
								<PgFields
									items={[
										['Purpose', humanize(p.purpose)],
										['Trigger', humanize(p.trigger)],
										['Method', p.label ?? humanize(p.methodType)],
										[
											'Client',
											p.clientNumber
												? `${p.clientNumber}${p.customerName ? `, ${p.customerName}` : ''}`
												: null,
										],
										['Service', p.serviceName ?? p.serviceCode],
										[
											'Invoice',
											p.invoiceNumber ? `${p.invoiceNumber} (ID ${p.invoiceId ?? '—'})` : null,
										],
										['EU DDP order', p.euDdpOrderId],
										['Requested by', p.requestedBy],
										['Created', formatDateTime(p.createdAt)],
										['Succeeded', formatDateTime(p.succeededAt)],
										['Failed', formatDateTime(p.failedAt)],
										['Cancelled', formatDateTime(p.cancelledAt)],
									]}
								/>
							</Section>
							<Section title="Amounts">
								<PgFields
									items={[
										['Base amount', formatMoney(p.baseAmount, p.currency)],
										[
											'Bank fee',
											`${formatMoney(p.feeAmount, p.currency)} (${formatPercent(p.feePercent)}${
												p.feeFixed ? ` + ${formatMoney(p.feeFixed)}` : ''
											})`,
										],
										['Total', formatMoney(p.totalAmount, p.currency)],
										[
											'Charged',
											p.chargedAmount !== null
												? formatMoney(p.chargedAmount, chargedCurrencyOf(p))
												: null,
										],
										['Refunded', formatMoney(p.refundedAmount, chargedCurrencyOf(p))],
										['Refundable now', formatMoney(refundable, chargedCurrencyOf(p))],
										['FX effective rate', p.fxEffectiveRate],
										[
											'FX margin',
											p.fxMarginPercent !== null ? formatPercent(p.fxMarginPercent) : null,
										],
										[
											'Received, EUR',
											p.receivedEur !== null ? formatMoney(p.receivedEur, 'EUR') : null,
										],
										[
											'Settlement',
											p.settledAt
												? `${formatDateTime(p.settledAt)}, net ${formatMoney(p.settlementNetAmount, p.settlementCurrency)}`
												: humanize(p.settlementMode),
										],
									]}
								/>
							</Section>
							<Section title="EAS">
								<PgFields
									items={[
										['EAS sync', <PgStatusChip key="eas" value={p.easSyncStatus} />],
										[
											'Invoice total',
											p.invoiceTotalEur !== null ? formatMoney(p.invoiceTotalEur, 'EUR') : null,
										],
										[
											'Paid before',
											p.invoicePaidBeforeEur !== null
												? formatMoney(p.invoicePaidBeforeEur, 'EUR')
												: null,
										],
										['Due date', formatDate(p.invoiceDueDate)],
										[
											'EAS reversal',
											p.easReversalEur !== null ? formatMoney(p.easReversalEur, 'EUR') : null,
										],
									]}
								/>
							</Section>

							<Section title={`Attempts (${p.attempts.length})`}>
								<PgMiniTable
									rows={p.attempts}
									getKey={(row) => row.id}
									columns={[
										{ header: '#', render: (row) => row.seq },
										{ header: 'Status', render: (row) => <PgStatusChip value={row.status} /> },
										{ header: 'Method', render: (row) => humanize(row.methodType) },
										{
											header: 'Amount',
											align: 'right',
											render: (row) => formatMoney(row.amount, row.currency),
										},
										{
											header: 'Airwallex',
											render: (row) => `${row.awxIntentId ?? '—'} / ${row.awxStatus ?? '—'}`,
										},
										{
											header: 'Error',
											render: (row) =>
												[row.failureCode, row.providerDeclineCode, row.errorMessage]
													.filter(Boolean)
													.join(' · ') || '—',
										},
										{ header: 'Created', render: (row) => formatDateTime(row.createdAt) },
									]}
								/>
							</Section>
							<Section title={`Refunds (${p.refunds.length})`}>
								<PgMiniTable
									rows={p.refunds}
									getKey={(row) => row.id}
									columns={[
										{ header: 'Status', render: (row) => <PgStatusChip value={row.status} /> },
										{
											header: 'Amount',
											align: 'right',
											render: (row) => formatMoney(row.amount, row.currency),
										},
										{
											header: 'EAS adjustment',
											align: 'right',
											render: (row) => formatMoney(row.easAdjustmentEur, 'EUR'),
										},
										{
											header: 'EAS sync',
											render: (row) => <PgStatusChip value={row.easSyncStatus} />,
										},
										{ header: 'Reason', render: (row) => row.reason },
										{ header: 'Created', render: (row) => formatDateTime(row.createdAt) },
									]}
								/>
							</Section>
							{p.fxConversion && (
								<Section title="FX conversion">
									<PgFields
										items={[
											['Status', <PgStatusChip key="fx" value={p.fxConversion.status} />],
											['Sell', formatMoney(p.fxConversion.sellAmount, p.fxConversion.sellCurrency)],
											[
												'Buy',
												p.fxConversion.buyAmount !== null
													? formatMoney(p.fxConversion.buyAmount, p.fxConversion.buyCurrency)
													: null,
											],
											['Client rate', p.fxConversion.clientRate],
											['Reference', p.fxConversion.shortReference],
											['Settled', formatDateTime(p.fxConversion.settledAt)],
											['Last error', p.fxConversion.lastError],
										]}
									/>
								</Section>
							)}
							<Section title={`E-mails (${p.notifications.length})`}>
								<PgMiniTable
									rows={p.notifications}
									getKey={(row) => row.id}
									columns={[
										{ header: 'Type', render: (row) => row.notificationType },
										{ header: 'Status', render: (row) => <PgStatusChip value={row.status} /> },
										{ header: 'Recipients', render: (row) => row.recipients.join(', ') },
										{ header: 'Error', render: (row) => row.error ?? '—' },
										{
											header: 'Sent',
											render: (row) => formatDateTime(row.sentAt ?? row.createdAt),
										},
									]}
								/>
							</Section>
							<Section title={`Outbox (${p.outbox.length})`}>
								<PgMiniTable
									rows={p.outbox}
									getKey={(row) => row.id}
									columns={[
										{ header: 'Message', render: (row) => row.messageType },
										{ header: 'Handler', render: (row) => row.handler },
										{
											header: 'Status',
											render: (row) => (
												<PgStatusChip
													value={row.status}
													label={
														row.parkReason
															? `${humanize(row.status)} (${row.parkReason})`
															: undefined
													}
												/>
											),
										},
										{ header: 'Attempts', align: 'right', render: (row) => row.attempts },
										{ header: 'Last error', render: (row) => row.lastError ?? '—' },
										{ header: 'Created', render: (row) => formatDateTime(row.createdAt) },
									]}
								/>
							</Section>
							{p.receipt && (
								<Section title="Receipt">
									<PgFields
										items={[
											['Status', humanize(p.receipt.status)],
											['File', p.receipt.fileName],
											['Generated', formatDateTime(p.receipt.generatedAt)],
											['Last opened', formatDateTime(p.receipt.lastAccessedAt)],
										]}
									/>
								</Section>
							)}
							<Divider sx={{ my: 2 }} />
							<Section title="Method snapshot">
								<PgJsonView value={p.methodSnapshot} maxHeight={160} />
							</Section>
						</>
					)}
				</DialogContent>
				<DialogActions>
					{transactionId && (
						<Button onClick={() => void load(transactionId)} disabled={loading}>
							Refresh
						</Button>
					)}
					<Button onClick={onClose}>Close</Button>
				</DialogActions>
			</Dialog>

			{p && (
				<>
					<RefundDialog
						open={refundOpen}
						payment={p}
						onClose={() => setRefundOpen(false)}
						onDone={() => {
							setRefundOpen(false);
							changed();
						}}
					/>
					<EasSyncDialog
						open={easSyncOpen}
						payment={p}
						onClose={() => setEasSyncOpen(false)}
						onDone={(next) => {
							setEasSyncOpen(false);
							changed(next);
						}}
					/>
				</>
			)}
			<PgConfirmDialog
				open={sendOpen}
				title="Send receipt"
				confirmLabel="Send"
				busy={acting === 'send'}
				onConfirm={() => void sendReceipt()}
				onClose={() => setSendOpen(false)}
				description="Sends the receipt by e-mail. By default it goes to the e-mail addresses of the customer; an explicit address is written to the audit log."
			>
				<TextField
					label="Send to (empty: the customer)"
					value={sendTo}
					onChange={(e) => setSendTo(e.target.value)}
					size="small"
					fullWidth
					type="email"
				/>
			</PgConfirmDialog>
		</>
	);
};
